/**
 * Sticky Add-to-Cart Bar
 * Mobile-only sticky bar that appears when the main buy box CTA scrolls out of view.
 * Delegates add-to-cart to the existing ProductFormComponent on the page.
 */

import { Component } from "./component.js";

class GRStickyAddToCart extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLSelectElement | null} */
  #frequencySelect = null;

  /** @type {any} */
  #variant = null;

  /** Whether a product has been added to cart — once true, sticky ATC stays hidden */
  #productAdded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#observeBuyBox();
    this.#syncFrequency();

    // Listen for subscription/one-time toggle + frequency changes
    document.addEventListener("selling-plan:update", this.#onSellingPlanUpdate);

    // Hide permanently once a product is added (sticky footer takes over)
    document.addEventListener("cart:update", this.#onCartUpdate);

    try {
      this.#variant = JSON.parse(this.dataset.variant || "null");
    } catch {
      this.#variant = null; // malformed data-variant shouldn't kill init
    }

    // Set initial price from the currently selected selling plan
    this.#syncInitialPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    document.removeEventListener(
      "selling-plan:update",
      this.#onSellingPlanUpdate,
    );
    document.removeEventListener("cart:update", this.#onCartUpdate);
  }

  /**
   * Watch gr-product-options — show sticky bar only when it scrolls completely
   * above the viewport
   */
  #observeBuyBox() {
    const productOptions = document.querySelector("gr-product-options");

    if (!productOptions) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.#hide();
        } else {
          // Only show when the element is above the viewport (scrolled past it)
          const isAboveViewport = entry.boundingClientRect.bottom < 0;
          if (isAboveViewport) {
            this.#show();
          } else {
            this.#hide();
          }
        }
      },
      { threshold: 0 },
    );

    this.#observer.observe(productOptions);

    // Start hidden
    this.#hide();
  }

  /**
   * Delegate click to the existing product form's submit button.
   */
  handleCTA() {
    const form = document.querySelector("product-form-component form");
    if (!form) return;

    const submitButton = /** @type {HTMLButtonElement | null} */ (
      form.querySelector("button[name='add']")
    );

    if (submitButton && !submitButton.disabled) {
      submitButton.click();
    }
  }

  /**
   * Scroll back up to the product options section.
   */
  handleFrequencyClick() {
    const productOptions = document.querySelector("gr-product-options");
    if (productOptions) {
      productOptions.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * Set the initial price based on whatever selling plan is selected on load.
   */
  #syncInitialPrice() {
    if (!this.#frequencySelect) {
      // No frequency select = one-time only product
      this.#setFrequencyText(null);
      this.#updatePrice(this.#variant?.price);
      return;
    }

    const selectedOption = this.#frequencySelect.selectedOptions[0];
    const sellingPlanId = selectedOption?.dataset.sellingPlanId || null;
    this.#updatePrice(this.#getSellingPlanPrice(sellingPlanId));
  }

  /**
   * Read the initial frequency from the select on the page.
   */
  #syncFrequency() {
    this.#frequencySelect = /** @type {HTMLSelectElement | null} */ (
      document.querySelector("gr-product-options [ref='frequencySelect']")
    );

    if (!this.#frequencySelect) return;

    const text = this.#frequencySelect.selectedOptions[0]?.textContent?.trim();
    this.#setFrequencyText(text);
  }

  /**
   * Handle selling-plan:update events from gr-product-options.
   * Covers both subscription/one-time toggle and frequency dropdown changes.
   * @param {Event} e
   */
  #onSellingPlanUpdate = (e) => {
    const detail = /** @type {any} */ (e).detail || {};
    const { type, frequency, sellingPlanId } = detail;

    if (type === "one-time") {
      this.#setFrequencyText(null);
      this.#updatePrice(this.#variant?.price);
    } else {
      this.#setFrequencyText(frequency);
      this.#updatePrice(this.#getSellingPlanPrice(sellingPlanId));
    }
  };

  /**
   * Find the subscription price for a given selling plan ID.
   * Falls back to the base variant price if no match.
   * @param {string | null} sellingPlanId
   * @returns {number}
   */
  #getSellingPlanPrice(sellingPlanId) {
    if (!this.#variant || !sellingPlanId) return this.#variant?.price ?? 0;

    const allocation = this.#variant.selling_plan_allocations?.find(
      (/** @type {any} */ a) =>
        String(a.selling_plan_id) === String(sellingPlanId),
    );

    return allocation?.price ?? this.#variant.price;
  }

  /**
   * Format cents and update the price display.
   * @param {number | undefined} cents
   */
  #updatePrice(cents) {
    const el = /** @type {HTMLElement | null} */ (this.refs.priceText);
    if (!el || cents == null) return;

    el.textContent = `$${(cents / 100).toFixed(0)}`;
  }

  /** @param {string | null | undefined} text */
  #setFrequencyText(text) {
    const el = /** @type {HTMLElement | null} */ (this.refs.frequencyText);
    if (!el) return;

    el.textContent = text ? `every ${text}` : "one time";
  }

  /**
   * Hide sticky ATC permanently once a product is added from this page.
   * The gr-pdp-sticky-footer takes over after that.
   */
  #onCartUpdate = (/** @type {any} */ e) => {
    const source = e.detail?.data?.source;
    if (source !== "product-form-component") return;
    if (e.detail?.data?.didError) return;

    this.#productAdded = true;
    this.#hide();
  };

  #show() {
    if (this.#productAdded) return;
    this.removeAttribute("hidden");
  }

  #hide() {
    this.setAttribute("hidden", "");
  }
}

if (!customElements.get("gr-sticky-add-to-cart")) {
  customElements.define("gr-sticky-add-to-cart", GRStickyAddToCart);
}
